import React, { useState, useEffect, useCallback } from 'react';
import { Inbox, X } from 'lucide-react';
import { apiService } from '../../services';
import { Message } from '../../types';

const ITEMS_PER_PAGE = 20;

export const MessagesPanel: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [archivedOnly, setArchivedOnly] = useState(false);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const skip = (page - 1) * ITEMS_PER_PAGE;
      const data = await apiService.getMessages(
        skip,
        ITEMS_PER_PAGE,
        unreadOnly,
        archivedOnly
      );
      setMessages(data);
    } catch (err) {
      console.error('Failed to fetch messages:', err);
      setError('Erreur lors du chargement des messages');
    } finally {
      setLoading(false);
    }
  }, [page, unreadOnly, archivedOnly]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleToggleRead = async (message: Message) => {
    try {
      const updated = await apiService.updateMessage(message.id, {
        is_read: !message.is_read,
      });
      setMessages((prev) =>
        prev.map((m) => (m.id === updated.id ? updated : m))
      );
    } catch (err) {
      console.error('Failed to update message:', err);
    }
  };

  const handleDelete = async (message: Message) => {
    if (!window.confirm('Archiver ce message ?')) return;
    try {
      await apiService.deleteMessage(message.id);
      setMessages((prev) => prev.filter((m) => m.id !== message.id));
    } catch (err) {
      console.error('Failed to delete message:', err);
    }
  };

  const handleRestore = async (message: Message) => {
    try {
      await apiService.updateMessage(message.id, { is_archived: false });
      setMessages((prev) => prev.filter((m) => m.id !== message.id));
    } catch (err) {
      console.error('Failed to restore message:', err);
    }
  };

  return (
    <div>
      <div className='flex items-center gap-6 mb-6'>
        <label className='flex items-center gap-2 text-sm text-white'>
          <input
            type='checkbox'
            checked={unreadOnly}
            onChange={(e) => {
              setPage(1);
              setUnreadOnly(e.target.checked);
            }}
          />
          Non lus uniquement
        </label>
        <label className='flex items-center gap-2 text-sm text-white'>
          <input
            type='checkbox'
            checked={archivedOnly}
            onChange={(e) => {
              setPage(1);
              setArchivedOnly(e.target.checked);
            }}
          />
          Voir les archives
        </label>
      </div>

      {loading ? (
        <div className='flex justify-center items-center py-16'>
          <div className='text-center'>
            <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4'></div>
            <p className='text-gray-600'>Chargement...</p>
          </div>
        </div>
      ) : error ? (
        <div className='p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm'>
          {error}
        </div>
      ) : messages.length === 0 ? (
        <div className='text-center py-16'>
          <Inbox className='w-16 h-16 mx-auto mb-4 text-gray-300' />
          <h2 className='text-xl font-semibold text-gray-900 mb-2'>
            Aucun message
          </h2>
          <p className='text-gray-600'>
            Aucun message ne correspond aux critères de recherche.
          </p>
        </div>
      ) : (
        <div className='space-y-3'>
          {messages.map((message) => {
            const isExpanded = expandedId === message.id;
            return (
              <div
                key={message.id}
                className='bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow'
              >
                <div
                  className='p-4 cursor-pointer'
                  onClick={() =>
                    setExpandedId(isExpanded ? null : message.id)
                  }
                >
                  <div className='flex items-start justify-between gap-4'>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center gap-2 mb-1'>
                        {!message.is_read && (
                          <span className='w-2 h-2 rounded-full bg-blue-600 flex-shrink-0' />
                        )}
                        <h3 className='font-semibold text-gray-900 truncate'>
                          {message.subject}
                        </h3>
                      </div>
                      <p className='text-sm text-gray-600'>
                        {message.name} —{' '}
                        <a
                          href={`mailto:${message.email}`}
                          onClick={(e) => e.stopPropagation()}
                          className='text-blue-600 hover:underline'
                        >
                          {message.email}
                        </a>
                      </p>
                      {!isExpanded && (
                        <p className='text-sm text-gray-500 mt-1 line-clamp-1'>
                          {message.message}
                        </p>
                      )}
                    </div>
                    <div className='flex items-center gap-2 flex-shrink-0'>
                      <span className='text-xs text-gray-500 whitespace-nowrap'>
                        {new Date(message.created_at).toLocaleDateString('fr-FR')}
                      </span>
                      {archivedOnly ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRestore(message);
                          }}
                          className='px-2 py-1 rounded text-xs font-medium whitespace-nowrap bg-green-100 text-green-700 hover:bg-green-200'
                        >
                          Restaurer
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleRead(message);
                            }}
                            className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${
                              message.is_read
                                ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                            }`}
                          >
                            {message.is_read ? 'Marquer non lu' : 'Marquer lu'}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(message);
                            }}
                            className='bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center hover:bg-red-600 transition-colors shadow-md'
                            title='Archiver'
                          >
                            <X className='w-4 h-4' />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {isExpanded && (
                    <p className='text-sm text-gray-700 mt-3 whitespace-pre-wrap border-t border-gray-100 pt-3'>
                      {message.message}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className='flex items-center justify-center gap-2 mt-6'>
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1 || loading}
          className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
        >
          Précédent
        </button>
        <span className='text-sm text-gray-600'>Page {page}</span>
        <button
          onClick={() => setPage((p) => p + 1)}
          disabled={loading || messages.length < ITEMS_PER_PAGE}
          className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
        >
          Suivant
        </button>
      </div>
    </div>
  );
};
